import { items as models } from '@/helpers/drawer'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ability } from '@/plugins/permissions'
import { useProducerStore } from './producer'
import DrawerItem from '@/types/DrawerItem'
import { Actions, Resources } from '@/plugins/permissions/typing'

/**
 * Recursively filter out the drawer items and their children by checking their
 * requirements and the current user's permissions.
 *
 * @param items
 * @returns filtered items
 */
const itemsFilter = (items: DrawerItem[]): DrawerItem[] => {
  return items.filter((item) => {
    if (
      // The item requires a producer to be set
      (item.needsProducer && useProducerStore().producer === null)

      // The item has a resource defined, thus requiring at least a read ability on it
      || (item.resource !== undefined && !ability.can('read', item.resource ?? ''))

      // The item requires some extra abilities
      || (item.requiredAbilities?.some((a) => {
        if (!a.includes('.')) {
          return true
        } // Malformed ability name

        const [action, resource] = a.split('.', 2) as [Actions, Resources]
        return !ability.can(action, resource)
      }))
    ) {
      return false
    }

    if (!item.children?.length) return true

    item.children = itemsFilter(item.children)

    return item.children.length > 0
  })
}

export const useDrawerStore = defineStore('drawer', () => {
  const open = ref(true)
  const lockedOpen = ref(false)
  const hovering = ref(false)
  const rail = ref(true)

  const openedItems = ref<string[]>([])

  const items = computed(() => itemsFilter(models.map(m => ({ ...m }))))

  function toggleDesktop() {
    lockedOpen.value = !lockedOpen.value
  }

  function toggleMobile() {
    open.value = !open.value
  }

  return { open, lockedOpen, hovering, rail, items, openedItems, toggleMobile, toggleDesktop }
})
